import React from "react";
import {SelectField} from "@reside/ui";
import {DocumentUploadCategory} from "@reside/reside-api-admission";

import {useDocumentUploadCategoriesQuery} from "../../../../../models/pcc";
import {useFacilitySettingsParams} from "../../../hooks/useFacilitySettingsParams";
import {AdminRoles} from "../../../../../atoms/admin-roles";
import {RoleNameEnum} from "../../../../../utils/types/common";
import {getWorkflowLabel} from "../../../../../atoms/start-workflow/workflowMapping";
import {useIsFacilitySettingsReadOnly} from "../FacilitySettingsReadOnlyContext";

type Props = Readonly<{
  workflowType: string;
  isPccEnabled?: boolean;
  hasArbitrationWorkflow: boolean;
}>;

const ADMISSION = "admission";
const ARBITRATION = "arbitration";

/**
 * Renders the PCC document upload category selector for a single workflow.
 * Must render inside FacilityPccMappingForm's Formik context; saved via
 * DocumentCategoryCardSaveButton → PUT /pcc-mapping.
 */
export const WorkflowDocumentCategoryField = ({
  workflowType,
  isPccEnabled,
  hasArbitrationWorkflow,
}: Props) => {
  const {facilityId} = useFacilitySettingsParams();
  const isReadOnly = useIsFacilitySettingsReadOnly();
  const normalizedType = workflowType.toLowerCase();

  const {
    data: documentUploadCategories = [],
    isLoading: isLoadingPccCategories,
  } = useDocumentUploadCategoriesQuery({
    facilityId,
    enabled: Boolean(isPccEnabled),
  });

  if (!isPccEnabled) {
    return null;
  }

  const commonSelectProps = {
    disabled: isReadOnly || isLoadingPccCategories,
    options: documentUploadCategories,
    getOptionValue: (category: DocumentUploadCategory) => category.id,
    getOptionLabel: (category: DocumentUploadCategory) => category.name,
  };

  if (normalizedType === ADMISSION) {
    return (
      <AdminRoles roles={[RoleNameEnum.RESIDE_ADMIN]}>
        <SelectField<DocumentUploadCategory>
          name="defaultDocumentCategoryIdentifier"
          label={
            isLoadingPccCategories
              ? "Loading PointClickCare Document Categories..."
              : "PointClickCare Document Category for Admission Packet"
          }
          placeholder={
            isLoadingPccCategories
              ? "Loading categories..."
              : "Select category for admission packet"
          }
          {...commonSelectProps}
        />
        {!hasArbitrationWorkflow && (
          <SelectField<DocumentUploadCategory>
            name="arbitrationCategoryId"
            label={
              isLoadingPccCategories
                ? "Loading PointClickCare Document Categories..."
                : "PointClickCare Document Category for Arbitration Packet (Optional)"
            }
            placeholder={
              isLoadingPccCategories
                ? "Loading categories..."
                : "Select category for arbitration packet (optional)"
            }
            {...commonSelectProps}
          />
        )}
      </AdminRoles>
    );
  }

  if (normalizedType === ARBITRATION && !hasArbitrationWorkflow) {
    return null;
  }

  return (
    <AdminRoles roles={[RoleNameEnum.RESIDE_ADMIN]}>
      <SelectField<DocumentUploadCategory>
        name={`workflowCategoryMappings.${normalizedType}`}
        label={
          isLoadingPccCategories
            ? "Loading PointClickCare Document Categories..."
            : `PointClickCare Document Category for ${getWorkflowLabel(
                workflowType,
              )} (Optional)`
        }
        placeholder={
          isLoadingPccCategories
            ? "Loading categories..."
            : `Select category for ${getWorkflowLabel(
                workflowType,
              ).toLowerCase()} (optional)`
        }
        {...commonSelectProps}
      />
    </AdminRoles>
  );
};
